// TrafficMappings.js
// Visual mappings for guest traffic sources, devices, and activity states.

const SOURCE_COLORS = {
  twitter: '#3bb0ff',
  x: '#3bb0ff',
  tiktok: '#c77dff',
  google: '#f6d76b',
  youtube: '#ff8f3f',
  facebook: '#6dc9ff',
  instagram: '#ff6f9c',
  linkedin: '#55a8ff',
  reddit: '#ff9153',
  direct: '#e6eef7',
  referral: '#7ef0c1',
  email: '#8ef1ff',
  default: '#9ad5ff',
};

const DEVICE_TRAILS = {
  desktop: { trailScale: 1.2, size: 2.1, jitter: 0.15 },
  tablet: { trailScale: 1.0, size: 1.7, jitter: 0.2 },
  mobile: { trailScale: 0.8, size: 1.2, jitter: 0.3 },
};

const ACTIVITY_FADE = {
  active: 1,
  idle: 0.6,
  background: 0.35,
};

const normalizeKey = (val) =>
  String(val || '')
    .trim()
    .toLowerCase();

const matchSourceKey = (guest = {}) => {
  const ref = normalizeKey(guest.referrer);
  const utm = normalizeKey(guest.utmSource || guest.utmMedium);
  const campaign = normalizeKey(guest.utmCampaign);

  const key =
    ref ||
    utm ||
    campaign ||
    normalizeKey(guest.utmContent) ||
    normalizeKey(guest.activityType);

  return key;
};

export const getSourceColor = (guest = {}) => {
  const key = matchSourceKey(guest);
  if (!key) return SOURCE_COLORS.default;

  if (key.includes('twitter') || key === 'x') return SOURCE_COLORS.twitter;
  if (key.includes('tiktok')) return SOURCE_COLORS.tiktok;
  if (key.includes('google')) return SOURCE_COLORS.google;
  if (key.includes('youtube')) return SOURCE_COLORS.youtube;
  if (key.includes('facebook') || key.includes('fb'))
    return SOURCE_COLORS.facebook;
  if (key.includes('instagram') || key.includes('ig'))
    return SOURCE_COLORS.instagram;
  if (key.includes('linkedin')) return SOURCE_COLORS.linkedin;
  if (key.includes('reddit')) return SOURCE_COLORS.reddit;
  if (key.includes('influencer') || key.includes('referral'))
    return SOURCE_COLORS.referral;
  if (key.includes('email')) return SOURCE_COLORS.email;
  if (key.includes('direct') || key === '(direct)') return SOURCE_COLORS.direct;

  return SOURCE_COLORS.default;
};

export const getAuraColor = (guest = {}) => {
  const base = getSourceColor(guest);
  return base; // keep 6-digit hex; alpha is added where used
};

export const getDeviceTraits = (guest = {}) => {
  const device = normalizeKey(guest.device);
  if (device.includes('desktop') || device.includes('mac') || device.includes('win'))
    return DEVICE_TRAILS.desktop;
  if (device.includes('tablet') || device.includes('ipad'))
    return DEVICE_TRAILS.tablet;
  if (device.includes('mobile') || device.includes('iphone') || device.includes('android'))
    return DEVICE_TRAILS.mobile;
  return DEVICE_TRAILS.desktop;
};

export const getActivityFade = (activityType = '') => {
  const key = normalizeKey(activityType);
  return ACTIVITY_FADE[key] ?? ACTIVITY_FADE.active;
};

export const getClusterKey = (guest = {}) => {
  const region = normalizeKey(guest.region);
  const utm = normalizeKey(guest.utmSource);
  const ref = normalizeKey(guest.referrer);
  return region || utm || ref || 'global';
};

export const mapSurgeColor = (guests = []) => {
  if (!guests.length) return SOURCE_COLORS.default;
  const tally = {};
  guests.forEach((g) => {
    const color = getSourceColor(g);
    tally[color] = (tally[color] || 0) + 1;
  });
  const dominant = Object.entries(tally).sort((a, b) => b[1] - a[1])[0];
  return dominant ? dominant[0] : SOURCE_COLORS.default;
};

export const isLowPowerDevice = () => {
  const hardwareThreads = typeof navigator !== 'undefined' ? navigator.hardwareConcurrency : null;
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
  const isMobileSafari =
    /iP(ad|hone|od)/i.test(ua) && /Safari/i.test(ua) && !/Chrome|CriOS/i.test(ua);
  const weakCpu = hardwareThreads && hardwareThreads < 4;
  return Boolean(weakCpu || isMobileSafari);
};

export const isConstellationForced = () =>
  String(process.env.REACT_APP_FORCE_CONSTELLATIONS || '')
    .trim()
    .toLowerCase() === 'true';

export default {
  SOURCE_COLORS,
  getSourceColor,
  getAuraColor,
  getDeviceTraits,
  getActivityFade,
  getClusterKey,
  mapSurgeColor,
  isLowPowerDevice,
};
